import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useConnectionNames } from '@/entities/connection';
import { groupByConnection, useDatasets } from '@/features/catalog-browser';
import type { NameLookup } from '@/features/catalog-browser';
import type { Capability } from '@/shared/api/generated/marketplace';
import { personName } from '@/shared/model/person';
import { Button } from '@/shared/ui/Button';
import { SidePanel } from '@/shared/ui/SidePanel';

import {
  useInstallations,
  useInstallPlugin,
  usePlugin,
  usePlugins,
  useRunPlugin,
  useUpgradePlan,
  useUpgradePlugin,
} from '../api/marketplace';
import {
  advisoryFindings,
  blockingFindings,
  capabilityLabelKey,
  installationFor,
  installBlockedReason,
  isTerminal,
  runBlockedReason,
  upgradeTargetFor,
} from '../model/marketplace';
import type { Permissions } from './MarketplaceScreen';

/** What the panel opens on: the plugin itself, its run form, or its upgrade. */
export type DrawerStart = 'view' | 'run' | 'upgrade';

/* One plugin, in the side panel the design draws beside the grid.
 *
 * **Install is choosing what to grant.** The capabilities the plugin declares are listed with what
 * each hands over, all ticked to start because that is what the plugin asked for; unticking one
 * narrows the grant, and the plugin then simply sees less. "Install and enable" is refused with
 * nothing ticked, because a plugin granted nothing cannot do anything.
 *
 * **Running and upgrading open here too**, from the Installed tab's buttons, so the run target is
 * chosen by qualified name from the catalogue — never typed as an id — and an upgrade shows which
 * grants carry to the new version before anything changes.
 */
export function PluginDrawer({
  pluginId,
  start = 'view',
  may,
  onClose,
}: {
  readonly pluginId: string;
  readonly start?: DrawerStart;
  readonly may: Permissions;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const plugin = usePlugin(pluginId);
  const plugins = usePlugins();
  const installations = useInstallations();

  const [granting, setGranting] = useState<readonly Capability[] | null>(null);
  const [upgrading, setUpgrading] = useState(start === 'upgrade');
  const [running, setRunning] = useState(start === 'run');
  const [datasetId, setDatasetId] = useState('');

  const install = useInstallPlugin();
  const run = useRunPlugin();
  const upgrade = useUpgradePlugin();

  const current = plugin.data;
  const installation =
    current === undefined
      ? undefined
      : installationFor(current, installations.data ?? []);
  const target =
    current === undefined ? undefined : upgradeTargetFor(current, plugins.data ?? []);
  const plan = useUpgradePlan(
    upgrading && target !== undefined ? pluginId : null,
    upgrading && target !== undefined ? target.version : null,
  );

  if (current === undefined) {
    return (
      <SidePanel label={t('marketplace.detail.panel')} onClose={onClose} head={null}>
        <p className="lq-mk__meta">
          {plugin.isError
            ? t('marketplace.error.pluginBody')
            : t('marketplace.detail.loading')}
        </p>
      </SidePanel>
    );
  }

  const grant = granting ?? current.declared_capabilities;
  const runReason = runBlockedReason(current, installation);
  const findings = [...blockingFindings(current), ...advisoryFindings(current)];

  const head = (
    <>
      <h2 className="lq-side__name">{current.name}</h2>
      <div className="lq-side__sub">
        {current.version} ·{' '}
        <span className="lq-mono" title={current.artifact_hash}>
          {current.artifact_hash.slice(0, 19)}…
        </span>
      </div>
    </>
  );

  return (
    <SidePanel label={current.name} onClose={onClose} head={head}>
      <div className="lq-mk__drawer">
        <span
          className="lq-mk__pill lq-mk__start"
          data-tone={
            current.state === 'published'
              ? 'ok'
              : current.state === 'rejected'
                ? 'error'
                : 'muted'
          }
        >
          <span className="lq-mk__pillDot" aria-hidden="true" />
          {t(`marketplace.state.${current.state}`)}
        </span>
        {isTerminal(current.state) ? (
          <p className="lq-mk__meta">{t(`marketplace.terminal.${current.state}`)}</p>
        ) : null}

        {findings.length > 0 ? (
          <div className="lq-mk__findings">
            {findings.map((finding) => (
              <div key={finding.code}>{finding.detail}</div>
            ))}
          </div>
        ) : null}

        {/* ---- install ---- */}
        {installation === undefined ? (
          <>
            <div className="lq-mk__legend">{t('marketplace.detail.toGrant')}</div>
            {current.declared_capabilities.map((code) => (
              <label className="lq-mk__grant" key={code}>
                <input
                  type="checkbox"
                  checked={grant.includes(code)}
                  disabled={!may.install || installBlockedReason(current) !== null}
                  onChange={(event) => {
                    install.reset();
                    setGranting(
                      event.target.checked
                        ? [...grant, code]
                        : grant.filter((each) => each !== code),
                    );
                  }}
                />
                <span>
                  <span className="lq-mk__capName">
                    {t(`marketplace.capName.${code.replace('.', '_')}`)}
                  </span>
                  <span className="lq-mk__grantDesc">{t(capabilityLabelKey(code))}</span>
                </span>
              </label>
            ))}
            {install.isError ? (
              <p className="lq-mk__error">{install.error.message}</p>
            ) : null}
            {may.install && installBlockedReason(current) === null ? (
              <Button
                tone="primary"
                className="lq-mk__start"
                disabled={grant.length === 0}
                busy={install.isPending}
                onClick={() => {
                  install.mutate({ pluginId, capabilities: grant });
                }}
              >
                {t('marketplace.action.installEnable')}
              </Button>
            ) : (
              <p className="lq-mk__meta">
                {t(installBlockedReason(current) ?? 'marketplace.detail.noInstall')}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="lq-mk__chips">
              <span className="lq-mk__meta">{t('marketplace.installed.granted')}</span>
              {installation.granted_capabilities.map((code) => (
                <span key={code} className="lq-mk__chip">
                  {t(`marketplace.capName.${code.replace('.', '_')}`)}
                </span>
              ))}
            </div>
            <p className="lq-mk__meta">
              {t('marketplace.detail.installedShort', {
                who: personName(installation.installed_by),
                when: new Date(installation.installed_at).toLocaleDateString(),
              })}
            </p>
          </>
        )}

        {/* ---- run ---- */}
        {installation !== undefined && may.run ? (
          running ? (
            <section className="lq-mk__section lq-mk__runForm">
              <div className="lq-mk__legend">{t('marketplace.run.title')}</div>
              <DatasetChoice
                value={datasetId}
                onChange={(chosen) => {
                  run.reset();
                  setDatasetId(chosen);
                }}
              />
              {run.isError ? <p className="lq-mk__error">{run.error.message}</p> : null}
              {run.data !== undefined ? (
                <p className="lq-mk__meta" role="status">
                  {t('marketplace.run.outcome', {
                    status: t(`marketplace.runStatus.${run.data.status}`),
                    findings: run.data.findings.length,
                  })}
                </p>
              ) : null}
              <div className="lq-mk__actions">
                <Button
                  size="compact"
                  onClick={() => {
                    setRunning(false);
                  }}
                >
                  {t('marketplace.action.cancel')}
                </Button>
                <Button
                  size="compact"
                  tone="primary"
                  disabled={datasetId.trim() === '' || runReason !== null}
                  busy={run.isPending}
                  onClick={() => {
                    run.mutate({ pluginId, datasetId: datasetId.trim() });
                  }}
                >
                  {t('marketplace.action.run')}
                </Button>
              </div>
            </section>
          ) : (
            <Button
              size="compact"
              className="lq-mk__start"
              disabled={runReason !== null}
              title={runReason === null ? undefined : t(runReason)}
              onClick={() => {
                setRunning(true);
              }}
            >
              {t('marketplace.action.runOnDataset')}
            </Button>
          )
        ) : null}

        {/* ---- upgrade ---- */}
        {installation !== undefined && target !== undefined && may.install ? (
          upgrading ? (
            <section className="lq-mk__section">
              <div className="lq-mk__legend">
                {t('marketplace.upgrade.title', {
                  from: current.version,
                  to: target.version,
                })}
              </div>
              {plan.data !== undefined ? (
                <>
                  <p className="lq-mk__meta">
                    {t('marketplace.upgrade.carried', {
                      list:
                        plan.data.carried_capabilities.join(', ') ||
                        t('marketplace.upgrade.none'),
                    })}
                  </p>
                  {plan.data.not_carried_capabilities.length > 0 ? (
                    <p className="lq-mk__warnText">
                      {t('marketplace.upgrade.notCarried', {
                        list: plan.data.not_carried_capabilities.join(', '),
                      })}
                    </p>
                  ) : null}
                  {plan.data.dropped_capabilities.length > 0 ? (
                    <p className="lq-mk__error">
                      {t('marketplace.upgrade.dropped', {
                        list: plan.data.dropped_capabilities.join(', '),
                      })}
                    </p>
                  ) : null}
                </>
              ) : null}
              {plan.isError ? <p className="lq-mk__error">{plan.error.message}</p> : null}
              {upgrade.isError ? (
                <p className="lq-mk__error">{upgrade.error.message}</p>
              ) : null}
              <div className="lq-mk__actions">
                <Button
                  size="compact"
                  onClick={() => {
                    setUpgrading(false);
                  }}
                >
                  {t('marketplace.action.cancel')}
                </Button>
                <Button
                  size="compact"
                  tone="primary"
                  disabled={plan.data === undefined}
                  busy={upgrade.isPending}
                  onClick={() => {
                    upgrade.mutate(
                      { pluginId, toVersion: target.version },
                      {
                        onSuccess: () => {
                          setUpgrading(false);
                        },
                      },
                    );
                  }}
                >
                  {t('marketplace.upgrade.confirm')}
                </Button>
              </div>
            </section>
          ) : (
            <Button
              size="compact"
              className="lq-mk__start"
              onClick={() => {
                setUpgrading(true);
              }}
            >
              {t('marketplace.action.upgradeTo', { version: target.version })}
            </Button>
          )
        ) : null}
      </div>
    </SidePanel>
  );
}

const PICKER_DATASETS = 200;

function DatasetChoice({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (datasetId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const datasets = useDatasets({ search: '', connectionId: null });
  const connections = useConnectionNames();

  const lookup: NameLookup = {
    status: connections.isPending
      ? 'loading'
      : connections.isError
        ? 'unreadable'
        : 'ready',
    names: connections.data ?? new Map<string, string>(),
  };
  const rows = datasets.data?.items ?? [];

  if (datasets.isPending) {
    return <p className="lq-mk__meta">{t('marketplace.run.datasetsLoading')}</p>;
  }
  /* A failed read and an unharvested estate are different facts, and rendering the first as the
     second sends somebody to harvest a connection they already have. */
  if (datasets.isError) {
    return <p className="lq-mk__error">{t('marketplace.run.datasetsUnreadable')}</p>;
  }
  if (rows.length === 0) {
    return <p className="lq-mk__meta">{t('marketplace.run.datasetsEmpty')}</p>;
  }

  return (
    <>
      <label className="lq-mk__field">
        {t('marketplace.run.datasetLabel')}
        <select
          className="lq-input"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        >
          <option value="">{t('marketplace.run.datasetUnchosen')}</option>
          {groupByConnection(rows, lookup).map((group) => (
            <optgroup
              key={group.connectionId}
              label={
                group.label.kind === 'named'
                  ? group.label.name
                  : t(`catalog.group.${group.label.kind}`)
              }
            >
              {group.rows.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.schema_name}.{row.object_name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      {/* The list is bounded, and says so: a picker that silently omits a dataset is worse than one
          that admits it stops. */}
      <span className="lq-mk__fine">
        {t('marketplace.run.datasetsNote', { limit: PICKER_DATASETS })}
      </span>
    </>
  );
}
